'use client';
import React from 'react';
import { DateTime } from 'luxon';

import Layout from '../lib/components/Layout';
import { Dividend } from '@benzinga/calendar-manager';
import type { DividendsQueryParamsInput } from '@benzinga/calendar-dividends-manager';
import { SessionContext } from '@benzinga/session-context';
import ColumnsDefinition from './ColumnsDef';
import { formatRequestDate } from '../lib/utils';
import { fetchDividends } from './Services';
import { CALENDAR_FETCH_PAGE_SIZE, useCalendarPagedFetch } from '../lib/useCalendarPagedFetch';

import { ICalendarProps, IDateRange } from '../lib/interfaces';

export const DividendsCalendarColumnsDef = ColumnsDefinition;
export const fetchDividendsData = fetchDividends;

export const DividendsCalendar: React.FC<ICalendarProps> = ({
  calendar,
  calendarData,
  calendarType,
  hasStockTableStyling,
  height,
  hiddenColumns,
  hideFilters,
  initialData,
  initialDateRange,
  initialTicker,
  leftSlot,
  onReady,
}) => {
  const session = React.useContext(SessionContext);
  const dateRange: IDateRange = initialDateRange ?? {
    date_from: formatRequestDate(new Date()),
    date_to: formatRequestDate(DateTime.now().plus({ days: 7 }).toJSDate()),
  };

  const initialParams = React.useMemo(
    () => ({
      dateFrom: dateRange.date_from,
      dateSort: 'ex' as const,
      dateTo: dateRange.date_to,
      ...(initialTicker && { symbols: initialTicker }),
    }),
    [dateRange.date_from, dateRange.date_to, initialTicker],
  );

  const { loading, onFilterChanged, onViewportChanged, setTableData, tableData } = useCalendarPagedFetch<
    Dividend,
    DividendsQueryParamsInput
  >({
    fetchPage: (params, page) =>
      fetchDividendsData(session.getSession(), {
        ...(params as DividendsQueryParamsInput),
        page,
        pageSize: CALENDAR_FETCH_PAGE_SIZE,
      }),
    initialData,
    initialParams,
  });

  React.useEffect(() => {
    if (calendarData?.serverSide) {
      setTableData(initialData ?? [], initialParams);
    }
  }, [calendarData?.serverSide, initialData, initialParams, setTableData]);

  function onTableReady() {
    if (initialData?.length) {
      setTableData(initialData, initialParams);
    } else {
      onFilterChanged({
        dateFrom: dateRange.date_from,
        dateSort: 'ex',
        dateTo: dateRange.date_to,
        ...(initialTicker && { symbols: initialTicker }),
      });
    }

    if (onReady) {
      setTimeout(() => onReady());
    }
  }

  return (
    <Layout
      calendar={calendar}
      calendarData={calendarData}
      calendarType={calendarType}
      columnDefs={ColumnsDefinition}
      defaultAdditionalParams={{ date_sort: 'ex' }}
      enableSearch
      hasStockTableStyling={hasStockTableStyling}
      height={height}
      hiddenColumns={hiddenColumns}
      hideFilters={hideFilters}
      initialDateRange={dateRange}
      leftSlot={leftSlot}
      onFilterChanged={onFilterChanged}
      onTableReady={onTableReady}
      tableData={tableData}
      tableLoading={loading}
      theme="modern"
      ticker={initialTicker}
      viewportChange={onViewportChanged}
    />
  );
};
